import Decimal from 'decimal.js';
import { IAdjustmentDetail, IAdjustmentDetails } from '../../redux/types/IAdjustmentDetails';

Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

const toDecimal = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') {
        return new Decimal(0);
    }

    try {
        return new Decimal(value);
    } catch {
        return new Decimal(0);
    }
};

export const formatShareDecimal = (value: Decimal) => value.toFixed(18);

const formatAmountDecimal = (value: Decimal) => value.toFixed(28);

export type AdjustmentDetailFromMatchInput = {
    ipNumber?: number | string | null;
    role?: string | null;
    sharePercentAdjusted?: string | number | null;
    pointsAdjusted?: string | number | null;
    amountAdjusted?: string | number | null;
    PayeeIpNameNumber?: number | string | null;
    Role?: string | null;
    SharePct?: string | number | null;
    PointsAllocated?: string | number | null;
    Amount?: string | number | null;
};

const normalizeFromRowMatchInput = (row: AdjustmentDetailFromMatchInput | unknown[]) => {
    if (Array.isArray(row)) {
        return {
            ipNumber: row[1],
            role: row[8] ?? '',
            sharePercentAdjusted: row[7] ?? '',
            pointsAdjusted: row[6] ?? '',
            amountAdjusted: row[5] ?? ''
        };
    }

    const typedRow = row as AdjustmentDetailFromMatchInput;
    return {
        ipNumber: typedRow.PayeeIpNameNumber ?? typedRow.ipNumber,
        role: typedRow.Role ?? typedRow.role ?? '',
        sharePercentAdjusted: typedRow.SharePct ?? typedRow.sharePercentAdjusted ?? '',
        pointsAdjusted: typedRow.PointsAllocated ?? typedRow.pointsAdjusted ?? '',
        amountAdjusted: typedRow.Amount ?? typedRow.amountAdjusted ?? ''
    };
};

export const isMatchingAdjustmentDetailFromRow = (
    existing: IAdjustmentDetail,
    candidate: AdjustmentDetailFromMatchInput | unknown[]
) => {
    const normalized = normalizeFromRowMatchInput(candidate);

    return String(existing.ipNumber ?? '') === String(normalized.ipNumber ?? '')
        && String(existing.role ?? '') === String(normalized.role ?? '')
        && String(existing.sharePercentAdjusted ?? '') === String(normalized.sharePercentAdjusted ?? '')
        && String(existing.pointsAdjusted ?? '') === String(normalized.pointsAdjusted ?? '')
        && String(existing.amountAdjusted ?? '') === String(normalized.amountAdjusted ?? '');
};

type AllocationUsageDetailRow = {
    PayeeIpNameNumber?: number | string | null;
    Role?: string | null;
};

export const getUniqueTemplateIpNumbers = (
    adjustmentDetails: IAdjustmentDetails[] = []
): string[] => {
    const templateIpNumbers: string[] = [];

    adjustmentDetails.forEach(detail => {
        detail.adjustmentDetailFrom.forEach(from => {
            const ipNumber = String(from.ipNumber ?? '');
            if (ipNumber && !templateIpNumbers.includes(ipNumber)) {
                templateIpNumbers.push(ipNumber);
            }
        });
    });

    return templateIpNumbers;
};

export const filterUsageDetailsByTemplateIpNumbers = <T extends AllocationUsageDetailRow>(
    usageDetails: T[] = [],
    templateIpNumbers: string[] = []
): T[] => {
    if (!templateIpNumbers.length) {
        return usageDetails;
    }

    return usageDetails.filter(item =>
        templateIpNumbers.includes(String(item.PayeeIpNameNumber ?? ''))
    );
};

export const getUsageDetailsForCheckboxSelection = <T extends AllocationUsageDetailRow>(
    usageDetails: T[] = [],
    adjustmentDetails: IAdjustmentDetails[] = [],
    useIpTemplateMode = false
): T[] => {
    if (!useIpTemplateMode || !adjustmentDetails.length) {
        return usageDetails;
    }

    const templateIpNumbers = getUniqueTemplateIpNumbers(adjustmentDetails);
    return filterUsageDetailsByTemplateIpNumbers(usageDetails, templateIpNumbers);
};

export const sortAdjustmentDetailsByAllocationOrder = (
    details: IAdjustmentDetails[],
    allocationRecords: { AllocationUsageHeaderId?: number }[] = []
): IAdjustmentDetails[] => {
    if (!details?.length || !allocationRecords?.length) {
        return details ?? [];
    }

    const orderByHeaderId = new Map(
        allocationRecords.map((record, index) => [String(record.AllocationUsageHeaderId), index])
    );

    return [...details].sort((a, b) => {
        const aIndex = orderByHeaderId.get(String(a.id));
        const bIndex = orderByHeaderId.get(String(b.id));

        if (aIndex === undefined && bIndex === undefined) {
            return 0;
        }
        if (aIndex === undefined) {
            return 1;
        }
        if (bIndex === undefined) {
            return -1;
        }
        return aIndex - bIndex;
    });
};

const clampSharePercent = (value: Decimal) => Decimal.max(0, value);

export const getTotalFromShare = (detail: IAdjustmentDetails) =>
    detail.adjustmentDetailFrom.reduce(
        (sum, from) => sum.plus(toDecimal(from.sharePercentAdjusted)),
        new Decimal(0)
    );

const getTotalFromAmount = (detail: IAdjustmentDetails) =>
    detail.adjustmentDetailFrom.reduce(
        (sum, from) => sum.plus(toDecimal(from.amountAdjusted)),
        new Decimal(0)
    );

const getTotalFromPoints = (detail: IAdjustmentDetails) =>
    detail.adjustmentDetailFrom.reduce(
        (sum, from) => sum.plus(toDecimal(from.pointsAdjusted)),
        new Decimal(0)
    );

export const getTotalToShare = (detail: IAdjustmentDetails) =>
    detail.adjustmentDetailTo.reduce(
        (sum, row) => sum.plus(toDecimal(row.sharePercentAdjusted)),
        new Decimal(0)
    );

const sharesMatchTotal = (shareSum: Decimal, total: Decimal) =>
    !total.isZero() && shareSum.equals(total);

const getBalancedToRowIndex = (toRows: IAdjustmentDetail[]) => {
    const adjustableIndices = toRows
        .map((row, idx) => ({ row, idx }))
        .filter(({ row }) => !row.isShareUserEntered)
        .map(({ idx }) => idx);

    return adjustableIndices.length > 0
        ? adjustableIndices[adjustableIndices.length - 1]
        : toRows.length - 1;
};

export const balanceToRowSharesToFromTotal = (detail: IAdjustmentDetails): IAdjustmentDetails => {
    if (detail.adjustmentDetailTo.length === 0) {
        return detail;
    }

    const totalFromShare = getTotalFromShare(detail);
    const targetIndex = getBalancedToRowIndex(detail.adjustmentDetailTo);

    const otherShareSum = detail.adjustmentDetailTo.reduce((sum, row, idx) => {
        if (idx === targetIndex) {
            return sum;
        }
        return sum.plus(toDecimal(row.sharePercentAdjusted));
    }, new Decimal(0));

    const remainder = clampSharePercent(totalFromShare.minus(otherShareSum));
    const adjustmentDetailTo = detail.adjustmentDetailTo.map((row, idx) => {
        if (idx !== targetIndex) {
            return row;
        }

        return {
            ...row,
            sharePercentAdjusted: formatShareDecimal(remainder)
        };
    });

    return { ...detail, adjustmentDetailTo };
};

const getFlexRowIndex = (toRows: IAdjustmentDetail[], excludedIndex = -1) => {
    const candidateIndices = toRows
        .map((row, idx) => ({ row, idx }))
        .filter(({ row, idx }) => idx !== excludedIndex && !row.isShareUserEntered)
        .map(({ idx }) => idx);

    if (candidateIndices.length === 0) {
        return -1;
    }

    return candidateIndices[candidateIndices.length - 1];
};

const scaleOtherToRowShares = (
    detail: IAdjustmentDetails,
    editedToRowIndex: number,
    editedShare: Decimal
): IAdjustmentDetail[] => {
    const totalFromShare = getTotalFromShare(detail);
    const remaining = clampSharePercent(totalFromShare.minus(editedShare));

    const otherIndices = detail.adjustmentDetailTo
        .map((_, idx) => idx)
        .filter(idx => idx !== editedToRowIndex);

    const otherShareSum = otherIndices.reduce(
        (sum, idx) => sum.plus(toDecimal(detail.adjustmentDetailTo[idx].sharePercentAdjusted)),
        new Decimal(0)
    );

    return detail.adjustmentDetailTo.map((row, idx) => {
        if (idx === editedToRowIndex) {
            return {
                ...row,
                sharePercentAdjusted: formatShareDecimal(editedShare),
                isShareUserEntered: true
            };
        }

        if (otherShareSum.greaterThan(0)) {
            const scaledShare = remaining.mul(
                toDecimal(row.sharePercentAdjusted).div(otherShareSum)
            );

            return {
                ...row,
                sharePercentAdjusted: formatShareDecimal(scaledShare),
                isShareUserEntered: false
            };
        }

        const isLastOtherRow = idx === otherIndices[otherIndices.length - 1];

        if (isLastOtherRow) {
            return {
                ...row,
                sharePercentAdjusted: remaining.greaterThan(0) ? formatShareDecimal(remaining) : '',
                isShareUserEntered: false
            };
        }

        return {
            ...row,
            sharePercentAdjusted: '',
            isShareUserEntered: false
        };
    });
};

export const syncAdjustmentDetailAmounts = (detail: IAdjustmentDetails): IAdjustmentDetails => {
    const totalFromShare = getTotalFromShare(detail);
    const totalFromAmount = getTotalFromAmount(detail);
    const totalFromPoints = getTotalFromPoints(detail);

    const rawValues = detail.adjustmentDetailTo.map(row => {
        const share = toDecimal(row.sharePercentAdjusted);

        if (totalFromShare.isZero() || share.isZero()) {
            return { amount: new Decimal(0), points: new Decimal(0) };
        }

        return {
            amount: share.div(totalFromShare).times(totalFromAmount),
            points: share.div(totalFromShare).times(totalFromPoints)
        };
    });

    const sharesAreBalanced = sharesMatchTotal(getTotalToShare(detail), totalFromShare);
    const balancedIndex = sharesAreBalanced ? getBalancedToRowIndex(detail.adjustmentDetailTo) : -1;

    const otherAmountSum = rawValues.reduce(
        (sum, { amount }, idx) => (idx === balancedIndex ? sum : sum.plus(toDecimal(formatAmountDecimal(amount)))),
        new Decimal(0)
    );
    const otherPointsSum = rawValues.reduce(
        (sum, { points }, idx) => (idx === balancedIndex ? sum : sum.plus(toDecimal(formatAmountDecimal(points)))),
        new Decimal(0)
    );

    const adjustmentDetailTo = detail.adjustmentDetailTo.map((row, idx) => {
        if (idx === balancedIndex) {
            return {
                ...row,
                amountAdjusted: formatAmountDecimal(totalFromAmount.minus(otherAmountSum)),
                pointsAdjusted: formatAmountDecimal(totalFromPoints.minus(otherPointsSum))
            };
        }

        return {
            ...row,
            amountAdjusted: formatAmountDecimal(rawValues[idx].amount),
            pointsAdjusted: formatAmountDecimal(rawValues[idx].points)
        };
    });

    return { ...detail, adjustmentDetailTo };
};

export const syncAdjustmentDetailShares = (detail: IAdjustmentDetails): IAdjustmentDetails => {
    const totalFromShare = getTotalFromShare(detail);
    const currentToShareSum = getTotalToShare(detail);

    if (detail.adjustmentDetailTo.length === 0) {
        return detail;
    }

    if (detail.adjustmentDetailTo.length >= 3) {
        return detail;
    }

    if (sharesMatchTotal(currentToShareSum, totalFromShare)) {
        return detail;
    }

    const userEnteredSum = detail.adjustmentDetailTo.reduce((sum, row) => {
        if (row.isShareUserEntered) {
            return sum.plus(toDecimal(row.sharePercentAdjusted));
        }
        return sum;
    }, new Decimal(0));

    const remaining = clampSharePercent(totalFromShare.minus(userEnteredSum));
    const flexRowIndex = getFlexRowIndex(detail.adjustmentDetailTo);

    if (flexRowIndex === -1) {
        return detail;
    }

    const adjustmentDetailTo = detail.adjustmentDetailTo.map((row, idx) => {
        if (row.isShareUserEntered) {
            return row;
        }

        if (idx === flexRowIndex) {
            return {
                ...row,
                sharePercentAdjusted: remaining.greaterThan(0) ? formatShareDecimal(remaining) : '',
                isShareUserEntered: false
            };
        }

        return {
            ...row,
            sharePercentAdjusted: '',
            isShareUserEntered: false
        };
    });

    return balanceToRowSharesToFromTotal({ ...detail, adjustmentDetailTo });
};

export const syncAdjustmentDetailDerivedValues = (detail: IAdjustmentDetails): IAdjustmentDetails =>
    syncAdjustmentDetailAmounts(syncAdjustmentDetailShares(detail));

export const syncAllAdjustmentDetailDerivedValues = (details: IAdjustmentDetails[]) =>
    details.map(syncAdjustmentDetailDerivedValues);

export const rescaleToRowSharesForFromTotalChange = (detail: IAdjustmentDetails): IAdjustmentDetails => {
    const totalFromShare = getTotalFromShare(detail);
    const currentToShareSum = getTotalToShare(detail);

    if (detail.adjustmentDetailTo.length === 0 || totalFromShare.isZero()) {
        return detail;
    }

    if (currentToShareSum.isZero()) {
        return syncAdjustmentDetailShares({
            ...detail,
            adjustmentDetailTo: detail.adjustmentDetailTo.map(row => ({
                ...row,
                isShareUserEntered: false
            }))
        });
    }

    const adjustmentDetailTo = detail.adjustmentDetailTo.map(row => ({
        ...row,
        sharePercentAdjusted: formatShareDecimal(
            toDecimal(row.sharePercentAdjusted).div(currentToShareSum).times(totalFromShare)
        ),
        isShareUserEntered: false
    }));

    return balanceToRowSharesToFromTotal({ ...detail, adjustmentDetailTo });
};

export const recalculateForFromTotalChange = (detail: IAdjustmentDetails): IAdjustmentDetails =>
    syncAdjustmentDetailAmounts(rescaleToRowSharesForFromTotalChange(detail));

export const applyAdjustmentHeaderPoolAndSegmentToToRows = (
    detail: IAdjustmentDetails,
    adjustmentPool?: string,
    adjustmentSegment?: string
): IAdjustmentDetails => {
    if (!detail.adjustmentDetailTo.length || (!adjustmentPool && adjustmentSegment === undefined)) {
        return detail;
    }

    return {
        ...detail,
        adjustmentDetailTo: detail.adjustmentDetailTo.map(row => ({
            ...row,
            ...(adjustmentPool ? { pool: adjustmentPool } : {}),
            ...(adjustmentSegment !== undefined ? { segment: adjustmentSegment } : {})
        }))
    };
};

export const applyAdjustmentHeaderPoolAndSegmentToAllDetails = (
    details: IAdjustmentDetails[] = [],
    adjustmentPool?: string,
    adjustmentSegment?: string
) =>
    details.map(detail => applyAdjustmentHeaderPoolAndSegmentToToRows(detail, adjustmentPool, adjustmentSegment));

export const normalizeAdjustmentDetailForSave = (
    detail: IAdjustmentDetails,
    adjustmentPool?: string,
    adjustmentSegment?: string
): IAdjustmentDetails =>
    normalizeAdjustmentDetailSharesForSave(
        applyAdjustmentHeaderPoolAndSegmentToToRows(detail, adjustmentPool, adjustmentSegment)
    );

export const normalizeAdjustmentDetailSharesForSave = (detail: IAdjustmentDetails): IAdjustmentDetails => {
    const derived = syncAdjustmentDetailDerivedValues(detail);

    const balanced = derived.adjustmentDetailTo.length >= 3
        ? derived
        : balanceToRowSharesToFromTotal(derived);

    return syncAdjustmentDetailAmounts(balanced);
};

export const normalizeAdjustmentDetailsForSave = (
    details: IAdjustmentDetails[] = [],
    adjustmentPool?: string,
    adjustmentSegment?: string
) =>
    details.map(detail => normalizeAdjustmentDetailForSave(detail, adjustmentPool, adjustmentSegment));

export const resetAutoCalculatedToRowShares = (details: IAdjustmentDetails[]): IAdjustmentDetails[] =>
    details.map(detail =>
        syncAdjustmentDetailDerivedValues({
            ...detail,
            adjustmentDetailTo: detail.adjustmentDetailTo.map(row =>
                row.isShareUserEntered
                    ? row
                    : { ...row, sharePercentAdjusted: '', isShareUserEntered: false }
            )
        })
    );

export const redistributeToRowShares = (
    detail: IAdjustmentDetails,
    editedToRowIndex: number,
    editedShareValue: string
): IAdjustmentDetails => {
    const clampedEdit = clampSharePercent(toDecimal(editedShareValue));

    const adjustmentDetailTo = detail.adjustmentDetailTo.length >= 3
        ? detail.adjustmentDetailTo.map((row, idx) =>
            idx === editedToRowIndex
                ? {
                    ...row,
                    sharePercentAdjusted: formatShareDecimal(clampedEdit),
                    isShareUserEntered: true
                }
                : row
        )
        : scaleOtherToRowShares(detail, editedToRowIndex, clampedEdit);

    return syncAdjustmentDetailAmounts(balanceToRowSharesToFromTotal({ ...detail, adjustmentDetailTo }));
};

export const addToRowWithRemainingShare = (detail: IAdjustmentDetails, newRow: IAdjustmentDetail): IAdjustmentDetails => {
    const totalFromShare = getTotalFromShare(detail);
    const currentToShareSum = getTotalToShare(detail);
    const remaining = clampSharePercent(totalFromShare.minus(currentToShareSum));

    return syncAdjustmentDetailAmounts({
        ...detail,
        adjustmentDetailTo: [
            ...detail.adjustmentDetailTo,
            {
                ...newRow,
                sharePercentAdjusted: remaining.greaterThan(0) ? formatShareDecimal(remaining) : '',
                isShareUserEntered: false
            }
        ]
    });
};

export const adjustmentDetailsDerivedValuesChanged = (
    previous: IAdjustmentDetails[],
    next: IAdjustmentDetails[]
) =>
    next.some((detail, detailIndex) =>
        detail.adjustmentDetailTo.some((toRow, toIndex) => {
            const previousRow = previous[detailIndex]?.adjustmentDetailTo[toIndex];
            return toRow.sharePercentAdjusted !== previousRow?.sharePercentAdjusted
                || toRow.amountAdjusted !== previousRow?.amountAdjusted
                || toRow.pointsAdjusted !== previousRow?.pointsAdjusted;
        })
    );
